/**
 * The quote's **Measure popover** — the number a measured service scales by,
 * looked up rather than typed (`builder-engine-and-consistency.md` §6.7 item
 * 4, board cb-19 cell 1).
 *
 * Two shapes, decided by the property, not by the quote:
 *
 *   **A · the property has one** — two radio cards: *The property's Bed area*
 *   (linked, so a re-measure moves the lines) or *A number for this quote
 *   only* (the property keeps its number).
 *   **B · it does not** — a warn band, the typed field, and *Save it on the
 *   property too*, which adds one row through the property's own measurement
 *   writer (`PUT /measurements/:property/:type`) and links the service to it.
 *
 * The locked strip under them is the server's projection at the measure on
 * screen. Nothing is written until Done.
 *
 * **One popover for a service and a package** (Aaron, review item 4.1). The
 * package group's header had a bare number box beside a second one for its
 * quantity, and nobody could tell what either was; it wears the service's
 * chip now and opens this. What it is about is a `MeasureSubject` — the
 * seven fields `QuoteServiceView` and `QuotePackageInstanceView` both carry
 * — and how the two differ is said by the caller, never asked here: the
 * header's tile, and whether the server has a projection to draw the strip
 * from (a service does; a package has none).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { request } from '../../lib/api.js'
import { cn } from '../../lib/cn.js'
import { formatDateOnly, formatNumber, formatRate } from '../../lib/format.js'
import { CheckRow } from '../../components/form/rows.js'
import { Band } from '../../components/primitives.js'
import { FieldShell } from '../../components/fields.js'
import { IconLock, IconRuler, IconWarn } from '../../frame/icons.js'
import { EditorActions, EditorHeader, EditorShell } from '../editor-shell.js'
import type { TileKind } from '../kind-tile.js'
import { UnitInput } from '../quantity/unit-input.js'
import { readNumber } from '../quantity/tiers.js'
import {
  AT_MEASURE, FOLLOWS_THE_PROPERTY, LINES_RESCALE, MEASURE_PLACEHOLDER, MEASURE_SOURCE,
  MEASURED_BY_TYPE, NO_MEASURE_YET, NO_MEASURE_YET_SUB, NOTHING_UNTIL_DONE, PROPERTY_KEEPS,
  PROPERTYS_MEASURE, QUOTE_ONLY, SAVE_ON_PROPERTY, SAVE_ON_PROPERTY_SUB, STRIP_MARGIN, STRIP_PRICE,
} from '../copy.js'

/** One row of `GET /measurements/:propertyId`. */
interface PropertyMeasurement {
  measurementTypeId: string
  id: string | null
  value: string | null
  source: string | null
  measuredOn: string | null
}

interface Projection { total: { amount: string; cost: string; marginPct: number | null } }

/**
 * The one builder popover that is not 480: cb-19 · 1 draws it at 420
 * (builder-engine §6.7 item 4). Named once, and held by its own drift row.
 */
const MEASURE_WIDTH = 420

export interface MeasureChoice {
  measure: number
  measurementId: string | null
  /** B with *Save it on the property too*: the row to add before linking. */
  saveToProperty: boolean
}

/**
 * What is being measured: a quote's service or a package on the quote. Both
 * views carry exactly these, so either is passed as it is.
 */
export interface MeasureSubject {
  id: string
  name: string
  measure: number | null
  measureUnit: string | null
  /** `Bed area` — the title, the radio and the empty field all say it. */
  measurementName: string | null
  measurementTypeId: string | null
  /** The property's measurement it follows now, or null for this quote's own. */
  measurementId: string | null
}

export function MeasurePopover({
  subject, tile = 'service', projectionPath, propertyId, place, canMeasure, anchor,
  onClose, onDone,
}: {
  subject: MeasureSubject
  /** The header's tile — what the measure is on. */
  tile?: TileKind
  /**
   * Where the server answers "the money at this measure" (`…/projection`),
   * for the locked strip; null draws none. A service has one. A package has
   * none, because nothing projects a whole package's visits at a measure
   * without writing them.
   */
  projectionPath: string | null
  propertyId: string | null
  /** "1080 Market St, Mechanicsburg" — the header's facts line. */
  place: string | null
  /** May this person edit the property? *Save it on the property too* is absent if not. */
  canMeasure: boolean
  anchor: HTMLElement | null
  onClose: () => void
  onDone: (choice: MeasureChoice) => Promise<boolean>
}) {
  const type = subject.measurementName ?? ''
  const unit = subject.measureUnit ?? ''
  const [found, setFound] = useState<PropertyMeasurement | null | undefined>(undefined)
  const [mode, setMode] = useState<'property' | 'quote'>('property')
  const [typed, setTyped] = useState('')
  const [save, setSave] = useState(canMeasure)
  const [projection, setProjection] = useState<Projection | null>(null)
  const [busy, setBusy] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  /* The property's own number, with its source and its date. */
  useEffect(() => {
    if (!propertyId || !subject.measurementTypeId) { setFound(null); return }
    void request<{ items: PropertyMeasurement[] }>(`/measurements/${propertyId}`)
      .then(({ items }) => {
        const row = items.find((one) => one.measurementTypeId === subject.measurementTypeId && one.id)
        setFound(row ?? null)
        const linked = row && subject.measurementId === row.id
        setMode(row && (linked || subject.measure === null) ? 'property' : 'quote')
        setTyped(subject.measure === null ? '' : formatNumber(subject.measure))
      })
      .catch(() => setFound(null))
  }, [propertyId, subject.measurementTypeId, subject.measurementId, subject.measure])

  const has = found !== null && found !== undefined && found.value !== null
  const propertyValue = has ? Number(found.value) : null
  const measure = has && mode === 'property' ? propertyValue! : readNumber(typed)

  /* B focuses the field it is asking for. */
  useEffect(() => {
    if (found === null) box.current?.querySelector('input')?.focus()
  }, [found])

  /* The strip: the server's money at the measure on screen, a quarter-second after the typing stops. */
  useEffect(() => {
    if (!projectionPath || !(measure > 0)) { setProjection(null); return }
    let live = true
    const timer = window.setTimeout(() => {
      void request<Projection>(`${projectionPath}?measure=${measure}`)
        .then((answer) => { if (live) setProjection(answer) })
        .catch(() => { if (live) setProjection(null) })
    }, 250)
    return () => { live = false; window.clearTimeout(timer) }
  }, [measure, projectionPath])

  async function done() {
    if (!(measure > 0)) { onClose(); return }
    setBusy(true)
    try {
      const ok = await onDone(has
        ? { measure, measurementId: mode === 'property' ? found.id : null, saveToProperty: false }
        : { measure, measurementId: null, saveToProperty: canMeasure && save && propertyId !== null })
      if (ok) onClose()
    } finally {
      setBusy(false)
    }
  }

  const facts = [place, subject.name].filter(Boolean).join(' · ')

  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label={MEASURED_BY_TYPE(type)}
      width={MEASURE_WIDTH}
      header={<EditorHeader kind={tile} icon={IconRuler} name={MEASURED_BY_TYPE(type)} facts={facts} />}
      footer={(
        <EditorActions
          fact={has ? LINES_RESCALE : NOTHING_UNTIL_DONE}
          onCancel={onClose}
          onDone={() => void done()}
          busy={busy}
        />
      )}
    >
      <div ref={box} data-measure-popover="" className="flex flex-col gap-3">
        {found === undefined && <p className="m-0 text-[12.5px] text-text-muted">Loading…</p>}

        {has && (
          <div role="radiogroup" aria-label={MEASURED_BY_TYPE(type)} className="flex flex-col gap-2.5">
            <RadioCard
              on={mode === 'property'}
              onPick={() => setMode('property')}
              title={PROPERTYS_MEASURE(type)}
              value={`${formatNumber(propertyValue!)} ${unit}`}
              sub={[
                found.source ? MEASURE_SOURCE[found.source] ?? found.source : null,
                found.measuredOn ? formatDateOnly(found.measuredOn) : null,
                FOLLOWS_THE_PROPERTY,
              ].filter(Boolean).join(' · ')}
            />
            <RadioCard
              on={mode === 'quote'}
              onPick={() => setMode('quote')}
              title={QUOTE_ONLY}
              sub={PROPERTY_KEEPS(`${formatNumber(propertyValue!)} ${unit}`)}
            >
              <UnitInput
                label={QUOTE_ONLY}
                value={typed}
                onChange={(value) => { setTyped(value); setMode('quote') }}
                unit={unit}
                width={150}
              />
            </RadioCard>
          </div>
        )}

        {found === null && (
          <>
            <Band tone="amber" icon={<IconWarn size={14} />} title={NO_MEASURE_YET(type)}>
              {NO_MEASURE_YET_SUB}
            </Band>
            <FieldShell label={type}>
              <UnitInput label={type} value={typed} onChange={setTyped} unit={unit} placeholder={MEASURE_PLACEHOLDER} />
            </FieldShell>
            {canMeasure && propertyId && (
              <CheckRow label={SAVE_ON_PROPERTY} sub={SAVE_ON_PROPERTY_SUB} checked={save} onChange={setSave} />
            )}
          </>
        )}

        {has && projection?.total && measure > 0 && (
          <div data-measure-strip="" className="flex flex-wrap items-center gap-x-3.5 gap-y-1 rounded-[10px] border border-line-faint bg-surface-alt px-3 py-2.5 text-[12px] text-text-muted">
            <span className="text-text-faint"><IconLock size={12} /></span>
            <span>{AT_MEASURE(formatNumber(measure), unit)}</span>
            <span>{STRIP_PRICE} <b className="tnum text-text">{formatRate(Number(projection.total.amount))}</b></span>
            {projection.total.marginPct !== null && (
              <span>{STRIP_MARGIN} <b className="tnum text-accent-dark">{projection.total.marginPct.toFixed(1)}%</b></span>
            )}
          </div>
        )}
      </div>
    </EditorShell>
  )
}

/** One choice as a card: a radio, a title, the value at the right, a sub-line, and whatever it holds. */
function RadioCard({ on, onPick, title, value, sub, children }: {
  on: boolean
  onPick: () => void
  title: string
  value?: string
  sub: string
  children?: ReactNode
}) {
  return (
    <div
      role="radio"
      aria-checked={on}
      tabIndex={0}
      onClick={onPick}
      onKeyDown={(event) => { if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); onPick() } }}
      className={cn('flex cursor-pointer gap-3 rounded-[12px] border px-3.5 py-3 text-left',
        on ? 'border-accent-line bg-accent-soft' : 'border-line bg-surface hover:border-line-strong')}
    >
      <span aria-hidden className={cn('mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2',
        on ? 'border-accent' : 'border-line-strong')}>
        {on && <span className="h-2 w-2 rounded-full bg-accent" />}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-2">
          <b className="text-[14px] font-semibold text-text">{title}</b>
          {value && <b className="tnum ml-auto text-[14px] font-bold text-text">{value}</b>}
        </span>
        <span className="text-[12px] text-text-muted">{sub}</span>
        {children && <span className="pt-2" onClick={(event) => event.stopPropagation()}>{children}</span>}
      </span>
    </div>
  )
}
